import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Loader2, Receipt } from 'lucide-react';
import { matchingAPI } from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';
import { useJobPolling, useJobResume, JobProgressLine, JobErrorBlock } from './JobPollingPanel';

const TALLY_LABELS = {
  seen: 'Seen',
  reconciled: 'Reconciled',
  no_candidate: 'No candidate',
  window_predates_feed_coverage: 'Predates feed coverage',
  ambiguous: 'Ambiguous',
  no_email_date: 'No email date',
  confirm_failed: 'Confirm failed',
  cohort_unbalanced: 'Cohort unbalanced',
  fx_rate_unavailable: 'FX rate unavailable',
  already_reconciled_since_preview: 'Already reconciled since preview',
  already_reconciled_externally: 'Already reconciled externally',
  settled_via_rent: 'Settled via rent',
  // Across ALL four awaiting_review_reason values this pass wrote -- NOT
  // only the ones visible in the "Suggested (pending review)" bucket below
  // (see suggested_pending_review_actionable's own label for that subset).
  suggested_pending_review: 'Suggested (any review reason, this pass)',
  suggested_pending_review_actionable: 'Suggested (pending review) -- actionable',
};

const TIER_LABELS = {
  tier1_own_entity: 'own entity',
  tier2_all_entities: 'all entities',
  cohort_assignment: 'cohort assignment',
};

// Human-readable text for each row's own awaiting_review_reason -- a
// suggested_pending_review row is no longer ALWAYS the reparse-resolved
// reason (reference/vendor-text amount-gap and brand-token arms suggest
// for their own reasons too); see services/receipt_paid_reconciliation.py's
// AWAITING_REVIEW_REASON_* constants for the full provenance.
const AWAITING_REVIEW_REASON_LABELS = {
  reparse_resolved_pending_review: 'reparse-resolved, awaiting human confirm in the matches review queue',
  reference_amount_gap_pending_review: 'reference matched but amount gap, awaiting human confirm in the matches review queue',
  vendor_text_amount_gap_pending_review: 'vendor-text matched but amount gap, awaiting human confirm in the matches review queue',
  vendor_brand_token_pending_review: 'brand-token evidence only, awaiting human confirm in the matches review queue',
};

// Short chip text for the same four reasons (Prompt, 2026-08-26 widened
// tab) -- AWAITING_REVIEW_REASON_LABELS above stays the long form (used as
// the chip's title/tooltip), this is what's actually printed on the row.
const AWAITING_REVIEW_REASON_CHIP_LABELS = {
  reparse_resolved_pending_review: 'reparse-resolved',
  reference_amount_gap_pending_review: 'reference: amount gap',
  vendor_text_amount_gap_pending_review: 'vendor text: amount gap',
  vendor_brand_token_pending_review: 'brand token',
};

// Chip color per reason -- reparse-resolved is the "working as designed,
// deterministic drain population" reason (neutral); the other three are
// the newly-widened, amount-gap-or-weaker-evidence reasons that never
// auto-confirm (amber, a visual "look before you click" cue).
const AWAITING_REVIEW_REASON_CHIP_VARIANT = {
  reparse_resolved_pending_review: 'secondary',
  reference_amount_gap_pending_review: 'outline',
  vendor_text_amount_gap_pending_review: 'outline',
  vendor_brand_token_pending_review: 'outline',
};

// Groups suggested-pending-review rows for display (Implementation item 4,
// "cohort-grouped, singles first"): a tier1_own_entity/tier2_all_entities/
// reference/vendor-text/brand-token row with no siblings sharing its
// (supplier, amount, currency) is a "single" and sorts first; rows sharing
// that key (the SAME cohort a cohort_assignment tier resolved, or simply
// two receipts that happen to coincide) are clustered together after,
// ordered by cluster size then key. Grouping is display-only -- never
// changes which matches are Confirm-able or how the shared filter scopes.
function groupSuggestedItemsForDisplay(items) {
  const byKey = new Map();
  items.forEach((item) => {
    const key = `${item.supplier || ''}|${item.amount ?? ''}|${item.currency || ''}`;
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key).push(item);
  });
  const singles = [];
  const cohorts = [];
  byKey.forEach((group, key) => {
    if (group.length === 1) singles.push({ key, items: group, isCohort: false });
    else cohorts.push({ key, items: group, isCohort: true });
  });
  cohorts.sort((a, b) => b.items.length - a.items.length);
  return [...singles, ...cohorts];
}

const CHAIN_POLL_INTERVAL_MS = 2000;

// Shared chunked-chain control (Prompt, 2026-08-26 v2): every suggestion
// census/repair/hygiene pass moved off a single synchronous call (timing
// out in prod at 2,651+ docs) onto services/chunked_chain.py -- same
// budget-boxed/resumable shape as the receipt-paid Preview/Apply above and
// RecurringCostMatchChainControl.jsx's own proven pattern, reused here as
// one hook instead of six hand-rolled copies. `result` is the chain's own
// accumulated `seed` once status=="complete" (finish_chain_run persists
// the chunk_fn's full return value there on every run, including the
// final non-truncated one) -- the SAME shape the old single-call preview
// functions used to return directly, so each card's own render logic is
// unchanged, only the trigger/polling underneath it.
function useSuggestionChain({ chainStart, chainStatus, chainStop, jobLatestOperation, onCompleted }) {
  const [chain, setChain] = useState(null);
  const [progress, setProgress] = useState(null);
  const [failed, setFailed] = useState(null);
  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [polling, setPolling] = useState(false);
  const pollRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setPolling(false);
  }, []);
  useEffect(() => stopPolling, [stopPolling]);

  const fetchChain = useCallback(() => {
    chainStatus().then(({ data }) => setChain(data?.chain || null)).catch(() => {
      // Non-fatal: the chain summary just won't update this tick.
    });
  }, [chainStatus]);

  const startJobPolling = useCallback((jobId) => {
    stopPolling();
    setPolling(true);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await matchingAPI.suggestedChainJobStatus(jobId);
        if (data.status === 'completed') {
          stopPolling();
          setProgress(null);
          setFailed(null);
          await fetchChain();
        } else if (data.status === 'failed') {
          stopPolling();
          setProgress(data.progress || null);
          setFailed(data.error);
          fetchChain();
        } else if (data.status === 'running') {
          setProgress(data.progress || null);
        }
      } catch (err) {
        stopPolling();
        setFailed(getErrorMessage(err));
      }
    }, CHAIN_POLL_INTERVAL_MS);
  }, [stopPolling, fetchChain]);

  // Recover an in-flight/last-finished job on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await matchingAPI.suggestedChainJobLatest(jobLatestOperation);
        if (cancelled) return;
        const job = data?.job;
        if (job?.status === 'running') {
          setProgress(job.progress || null);
          startJobPolling(job._id || job.id);
        } else if (job?.status === 'failed') {
          setProgress(job.progress || null);
          setFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchChain(); }, [fetchChain]);

  // Keep polling chain status while running, including the gap between
  // one run finishing and its successor starting (no job is "running" to
  // poll in that window).
  useEffect(() => {
    if (chain?.status !== 'running') return undefined;
    const id = setInterval(fetchChain, CHAIN_POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [chain?.status, fetchChain]);

  const chainStatusValue = chain?.status;
  useEffect(() => {
    if (chainStatusValue === 'complete' && onCompleted) onCompleted(chain);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainStatusValue]);

  const run = async () => {
    setStarting(true);
    setFailed(null);
    try {
      const { data } = await chainStart();
      if (data.job_id) {
        startJobPolling(data.job_id);
      } else if (data.status === 'chain_running') {
        toast.error('Already running -- it will continue on its own.');
      } else if (data.status === 'chain_unavailable') {
        toast.error('Chain became unavailable while starting -- try again.');
      }
      fetchChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setStarting(false);
    }
  };

  const stop = async () => {
    if (!chain?._id) return;
    if (!window.confirm('Stop this running pass? The current run finishes its time window normally.')) return;
    setStopping(true);
    try {
      await chainStop(chain._id);
      toast.success('Stop requested -- it will halt after its current run finishes.');
      fetchChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setStopping(false);
    }
  };

  const running = chain?.status === 'running';
  const complete = chain?.status === 'complete';
  const result = complete ? chain?.seed : null;
  const busy = starting || polling;

  return { chain, result, progress, failed, running, complete, busy, stopping, run, stop };
}

// Renders a chain's own in-flight progress (scanned so far this run) or a
// failure message -- nothing when idle/complete, so cards stay quiet once
// a result is showing. Shared across all six suggestion chains.
function ChainProgressLine({ chain, testIdPrefix }) {
  if (chain.failed) {
    return (
      <span className="text-red-600 dark:text-red-400" data-testid={`${testIdPrefix}-error`}>
        {chain.failed}
      </span>
    );
  }
  if (!chain.running && !chain.progress) return null;
  const scanned = chain.progress?.total_scanned ?? chain.progress?.docs_processed_this_run;
  return (
    <span className="text-muted-foreground" data-testid={`${testIdPrefix}-progress`}>
      running (run {chain.chain?.runs_count || 1}){scanned != null ? `, ${scanned} scanned so far` : ''}
      {chain.chain?.stop_requested ? ' -- stopping' : ''}
    </span>
  );
}

// One row per standing "Suggested (pending review)" match (GET /matches/
// suggested-pending-review) -- Confirm routes through /matches/{id}/confirm-
// suggested (approves the invoice's review, then confirms, both scoped
// server-side to this exact provenance); Reject reuses the ordinary
// PUT /matches/{id}/reject as-is (marks the suggestion dismissed, the
// invoice's own review_status is never touched, never a delete).
function SuggestedPendingReviewRow({ item, selected, onToggleSelect, onConfirm, onReject, busy }) {
  return (
    <div
      className="flex items-center justify-between gap-2 border-t py-2 first:border-t-0 first:pt-0 flex-wrap"
      data-testid={'suggested-pending-review-row-' + item.match_id}
    >
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <Checkbox
          checked={!!selected}
          onCheckedChange={function () { onToggleSelect(item.match_id); }}
          aria-label={'Select suggestion ' + item.match_id}
          data-testid={'select-suggested-' + item.match_id}
        />
        <span className="font-mono text-blue-700 dark:text-blue-400">
          {item.invoice_id} · {item.supplier} · {item.amount != null ? item.amount : '?'}
          {item.currency ? ' ' + item.currency : ''} · candidate tx {item.transaction_id}
          {item.tier && <> · {TIER_LABELS[item.tier] || item.tier}</>}
          {item.tie_broken && <> · tie-broken</>}
        </span>
        {item.awaiting_review_reason && (
          <Badge
            variant={AWAITING_REVIEW_REASON_CHIP_VARIANT[item.awaiting_review_reason] || 'outline'}
            className="text-xs"
            data-testid={'reason-chip-' + item.match_id}
            title={AWAITING_REVIEW_REASON_LABELS[item.awaiting_review_reason] || item.awaiting_review_reason}
          >
            {AWAITING_REVIEW_REASON_CHIP_LABELS[item.awaiting_review_reason] || item.awaiting_review_reason}
          </Badge>
        )}
        {item.matched_amount != null && item.amount != null && item.matched_amount !== item.amount && (
          <span
            className="text-xs text-amber-700 dark:text-amber-400"
            data-testid={'amount-gap-' + item.match_id}
            title="Matched transaction amount differs from the invoice total -- confirming books this as a partial allocation, never a false full payment."
          >
            matched {item.matched_amount}{item.currency ? ' ' + item.currency : ''} of {item.amount}
            {item.currency ? ' ' + item.currency : ''}
          </span>
        )}
        {item.cohort_recompute_unassigned && (
          <Badge
            variant="destructive"
            className="text-xs"
            data-testid={'cohort-unassigned-' + item.match_id}
            title="A cohort recompute could not give this receipt its own distinct transaction -- confirming may 409 if a sibling already claimed this candidate."
          >
            cohort: unassigned
          </Badge>
        )}
      </div>
      <div className="flex items-center gap-1">
        <Button
          size="sm" variant="default" disabled={busy}
          onClick={function () { onConfirm(item.match_id); }}
          data-testid={'confirm-suggested-' + item.match_id}
        >
          Confirm
        </Button>
        <Button
          size="sm" variant="outline" disabled={busy}
          onClick={function () { onReject(item.match_id); }}
          data-testid={'reject-suggested-' + item.match_id}
        >
          Reject
        </Button>
      </div>
    </div>
  );
}

export function ReceiptPaidReconciliationPanel() {
  const [preview, setPreview] = useState(null);
  const [previewStarting, setPreviewStarting] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(null);
  const [previewProgress, setPreviewProgress] = useState(null);

  const [applyStarting, setApplyStarting] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [applyFailed, setApplyFailed] = useState(null);
  const [applyProgress, setApplyProgress] = useState(null);

  // Standing "N awaiting your review" badge (routes/admin_receipt_paid_
  // reconciliation.py::receipt_paid_awaiting_review_count) -- a live count,
  // independent of any single Preview/Apply pass's own tallies below, so it
  // reads correctly on page load even if Preview has never been run this
  // session. Refreshed on mount and again whenever Apply completes (Apply
  // is the only action on this card that can create new suggested matches).
  const [awaitingReviewCount, setAwaitingReviewCount] = useState(null);
  const refreshAwaitingReviewCount = useCallback(() => {
    matchingAPI.receiptPaidAwaitingReviewCount()
      .then(({ data }) => setAwaitingReviewCount(data.count))
      .catch(() => {
        // Non-critical secondary metric -- Preview/Apply's own error
        // handling already surfaces a failure for the primary action.
      });
  }, []);
  useEffect(() => { refreshAwaitingReviewCount(); }, [refreshAwaitingReviewCount]);

  // Suggested (pending review) bucket: the actionable list backing the
  // badge above -- same standing population, enriched per row for display
  // (GET /matches/suggested-pending-review). Refreshed on mount and after
  // every confirm/reject action on this bucket, same pattern as the count.
  const [suggestedItems, setSuggestedItems] = useState([]);
  const [selectedSuggestedIds, setSelectedSuggestedIds] = useState(() => new Set());
  const [suggestedActionBusy, setSuggestedActionBusy] = useState(false);

  const refreshSuggestedList = useCallback(() => {
    matchingAPI.suggestedPendingReviewList()
      .then(({ data }) => setSuggestedItems((data && data.items) || []))
      .catch(() => {
        // Non-critical secondary list -- the standing badge above still
        // carries the count even if this enriched list fails to load.
      });
  }, []);
  useEffect(() => { refreshSuggestedList(); }, [refreshSuggestedList]);

  // Prune the selection to whatever the list actually still contains --
  // a refresh after a confirm/reject (or a concurrent operator/process
  // acting on the same match) must never leave a dangling selected id,
  // same rule as ReconciliationPage's own select-all pruning.
  useEffect(() => {
    setSelectedSuggestedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(suggestedItems.map((it) => it.match_id));
      const next = new Set();
      let changed = false;
      prev.forEach((id) => { if (visible.has(id)) next.add(id); else changed = true; });
      return changed ? next : prev;
    });
  }, [suggestedItems]);

  const allSuggestedSelected = suggestedItems.length > 0
    && suggestedItems.every((it) => selectedSuggestedIds.has(it.match_id));

  const handleToggleSelectSuggested = (matchId) => {
    setSelectedSuggestedIds((prev) => {
      const next = new Set(prev);
      if (next.has(matchId)) next.delete(matchId); else next.add(matchId);
      return next;
    });
  };

  const handleToggleSelectAllSuggested = () => {
    setSelectedSuggestedIds(() => (
      allSuggestedSelected ? new Set() : new Set(suggestedItems.map((it) => it.match_id))
    ));
  };

  const handleConfirmOneSuggested = async (matchId) => {
    setSuggestedActionBusy(true);
    try {
      await matchingAPI.confirmSuggested(matchId);
      toast.success('Confirmed ' + matchId);
      setSelectedSuggestedIds((prev) => {
        const next = new Set(prev);
        next.delete(matchId);
        return next;
      });
      refreshSuggestedList();
      refreshAwaitingReviewCount();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSuggestedActionBusy(false);
    }
  };

  const handleRejectOneSuggested = async (matchId) => {
    setSuggestedActionBusy(true);
    try {
      await matchingAPI.reject(matchId);
      toast.success('Dismissed ' + matchId);
      setSelectedSuggestedIds((prev) => {
        const next = new Set(prev);
        next.delete(matchId);
        return next;
      });
      refreshSuggestedList();
      refreshAwaitingReviewCount();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSuggestedActionBusy(false);
    }
  };

  const handleBulkConfirmSuggested = async () => {
    const ids = Array.from(selectedSuggestedIds);
    if (ids.length === 0) return;
    if (!window.confirm(
      `Confirm ${ids.length} selected suggestion${ids.length !== 1 ? 's' : ''}? Each approves ` +
      'the invoice\'s review and confirms the match in one step -- a suggestion no longer ' +
      'confirmable (already claimed elsewhere, allocation cap, etc.) is skipped with a reason, ' +
      'not silently dropped, and the rest of the batch still proceeds.'
    )) return;
    setSuggestedActionBusy(true);
    try {
      const { data } = await matchingAPI.bulkConfirmSuggested(ids);
      const confirmedCount = data?.confirmed_count || 0;
      const skippedCount = data?.skipped_count || 0;
      if (confirmedCount > 0) {
        let msg = `Confirmed ${confirmedCount} suggestion${confirmedCount !== 1 ? 's' : ''}`;
        if (skippedCount > 0) msg += ` (${skippedCount} skipped, see below)`;
        toast.success(msg);
      }
      if (skippedCount > 0) {
        (data.skipped || []).forEach((s) => { toast.error(`Skipped ${s.match_id}: ${s.reason}`); });
      }
      setSelectedSuggestedIds(new Set());
      refreshSuggestedList();
      refreshAwaitingReviewCount();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSuggestedActionBusy(false);
    }
  };

  // Vendor-text cohort recompute: repairs a Suggested (pending review) match
  // the vendor-text arm wrote before its own one-to-one cohort solver
  // existed (services/receipt_paid_reconciliation.py::recompute_suggested_
  // pending_review_vendor_text_cohorts). Preview-then-Apply, same "don't
  // auto-correct evidence" shape as the card's own main Preview/Apply.
  const [cohortRecomputePreview, setCohortRecomputePreview] = useState(null);
  const [cohortRecomputeBusy, setCohortRecomputeBusy] = useState(false);

  const handlePreviewCohortRecompute = async () => {
    setCohortRecomputeBusy(true);
    try {
      const { data } = await matchingAPI.suggestedVendorTextCohortRecomputePreview();
      setCohortRecomputePreview(data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setCohortRecomputeBusy(false);
    }
  };

  const handleApplyCohortRecompute = async () => {
    const t = cohortRecomputePreview?.tallies;
    if (!t || (t.reassigned === 0 && t.flagged_unassigned === 0)) return;
    if (!window.confirm(
      `Apply cohort recompute? This will reassign ${t.reassigned} suggestion(s) to their own ` +
      `distinct transaction and flag ${t.flagged_unassigned} that still can't be given one. ` +
      'Nothing is deleted -- superseded suggestions stay in the database, reversible.'
    )) return;
    setCohortRecomputeBusy(true);
    try {
      const { data } = await matchingAPI.suggestedVendorTextCohortRecomputeApply();
      setCohortRecomputePreview(null);
      toast.success(
        `Reassigned ${data?.tallies?.reassigned || 0}, flagged ${data?.tallies?.flagged_unassigned || 0}`
      );
      refreshSuggestedList();
      refreshAwaitingReviewCount();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setCohortRecomputeBusy(false);
    }
  };

  // Suggestion doc shape census (Phase 0, read-only) + reparse-precedence
  // repair: the permanent answer to "this tab/count say 0 but a pass
  // tally said N" -- see services/receipt_paid_reconciliation.py::
  // census_chunk_fn / _repair_chunk. Rendered unconditionally (NOT gated
  // on suggestedItems.length), since the whole point is diagnosing why
  // the tab above might be empty despite real stored suggestions
  // existing. Both are budget-boxed chains now (Prompt, 2026-08-26 v2).
  const censusChain = useSuggestionChain({
    chainStart: matchingAPI.suggestedCensusChainStart,
    chainStatus: matchingAPI.suggestedCensusChainStatus,
    chainStop: matchingAPI.suggestedCensusChainStop,
    jobLatestOperation: 'census',
  });

  const repairPreviewChain = useSuggestionChain({
    chainStart: matchingAPI.suggestedReparsePrecedenceRepairPreviewChainStart,
    chainStatus: matchingAPI.suggestedReparsePrecedenceRepairPreviewChainStatus,
    chainStop: matchingAPI.suggestedReparsePrecedenceRepairPreviewChainStop,
    jobLatestOperation: 'repair_preview',
  });

  const repairApplyChain = useSuggestionChain({
    chainStart: matchingAPI.suggestedReparsePrecedenceRepairApplyChainStart,
    chainStatus: matchingAPI.suggestedReparsePrecedenceRepairApplyChainStatus,
    chainStop: matchingAPI.suggestedReparsePrecedenceRepairApplyChainStop,
    jobLatestOperation: 'repair_apply',
    onCompleted: (completedChain) => {
      toast.success(`Repaired ${completedChain?.seed?.tallies?.repaired ?? 0}`);
      refreshSuggestedList();
      refreshAwaitingReviewCount();
      censusChain.run();
    },
  });

  const handleApplyReparsePrecedenceRepair = () => {
    const t = repairPreviewChain.result?.tallies;
    if (!t || t.stale_found === 0) return;
    if (!window.confirm(
      `Apply reparse-precedence repair? This will fix ${t.stale_found} suggestion(s) whose ` +
      'awaiting_review_reason predates d95bf3ca, making them visible in the Suggested (pending ' +
      'review) bucket above and the standing count. Every other field (tier, transaction, ' +
      'evidence) is left exactly as originally written -- only visibility was wrong.'
    )) return;
    repairApplyChain.run();
  };

  // Auto-confirm tier (Arm B): deterministic tier-1-only qualification over
  // the exact same suggested backlog above. Flag defaults OFF; status
  // carries both the flag state and the last completed Apply run's counts/
  // breakdown, refreshed on mount and after every flag toggle / Apply.
  const [acStatus, setAcStatus] = useState(null);
  const [acFlagBusy, setAcFlagBusy] = useState(false);
  const [acPreview, setAcPreview] = useState(null);
  const [acPreviewStarting, setAcPreviewStarting] = useState(false);
  const [acPreviewFailed, setAcPreviewFailed] = useState(null);
  const [acPreviewProgress, setAcPreviewProgress] = useState(null);
  const [acApplyResult, setAcApplyResult] = useState(null);
  const [acApplyStarting, setAcApplyStarting] = useState(false);
  const [acApplyFailed, setAcApplyFailed] = useState(null);
  const [acApplyProgress, setAcApplyProgress] = useState(null);

  const refreshAcStatus = useCallback(() => {
    matchingAPI.suggestedAutoconfirmStatus()
      .then(({ data }) => setAcStatus(data))
      .catch(() => {
        // Non-critical secondary status -- Preview/Apply's own error
        // handling already surfaces a failure for the primary action.
      });
  }, []);
  useEffect(() => { refreshAcStatus(); }, [refreshAcStatus]);

  const toggleAcFlag = async (enabled) => {
    if (enabled && !window.confirm(
      'Enable suggested-match auto-confirm? From the next scheduled tick (and any Run Now / ' +
      'Apply here) onward, every qualifying suggestion is confirmed with zero clicks -- ' +
      'deterministic tier-1 matches only, everything else stays in the Suggested (pending ' +
      'review) bucket above for a human.'
    )) return;
    setAcFlagBusy(true);
    try {
      const { data } = await matchingAPI.setSuggestedAutoconfirmEnabled(enabled);
      setAcStatus(data);
      toast.success(enabled ? 'Auto-confirm enabled' : 'Auto-confirm disabled');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setAcFlagBusy(false);
    }
  };

  const acPreviewPoll = useJobPolling({
    fetchStatus: matchingAPI.suggestedAutoconfirmJobStatus,
    onCompleted: useCallback((result) => {
      setAcPreview(result);
      setAcPreviewFailed(null);
      setAcPreviewProgress(null);
    }, []),
    onFailed: useCallback((error) => {
      setAcPreviewFailed(error);
      setAcPreviewProgress(null);
    }, []),
    onProgress: useCallback((progress) => { setAcPreviewProgress(progress); }, []),
  });

  const acApplyPoll = useJobPolling({
    fetchStatus: matchingAPI.suggestedAutoconfirmJobStatus,
    onCompleted: useCallback((result) => {
      setAcApplyResult(result);
      setAcApplyFailed(null);
      setAcApplyProgress(null);
      refreshAcStatus();
      refreshSuggestedList();
      refreshAwaitingReviewCount();
    }, [refreshAcStatus, refreshSuggestedList, refreshAwaitingReviewCount]),
    onFailed: useCallback((error) => {
      setAcApplyFailed(error);
      setAcApplyProgress(null);
    }, []),
    onProgress: useCallback((progress) => { setAcApplyProgress(progress); }, []),
  });

  useJobResume({ fetchLatest: useCallback(() => matchingAPI.suggestedAutoconfirmJobLatest(true), []), poll: acPreviewPoll });
  useJobResume({ fetchLatest: useCallback(() => matchingAPI.suggestedAutoconfirmJobLatest(false), []), poll: acApplyPoll });

  const runAcPreview = async () => {
    setAcPreviewStarting(true);
    setAcPreviewFailed(null);
    setAcPreviewProgress(null);
    setAcApplyResult(null);
    setAcApplyFailed(null);
    try {
      const { data } = await matchingAPI.suggestedAutoconfirmStart(true);
      if (data.job_id) acPreviewPoll.start(data.job_id);
    } catch (err) {
      setAcPreviewFailed(getErrorMessage(err) || 'Failed to start preview.');
    } finally {
      setAcPreviewStarting(false);
    }
  };

  const acPreviewBusy = acPreviewStarting || acPreviewPoll.polling;

  const runAcApply = async () => {
    if (!window.confirm(
      'Run auto-confirm now ("Drain now")? Every currently-qualifying suggestion (deterministic ' +
      'tier-1 match only) is confirmed immediately; everything else is left exactly as-is in the ' +
      'Suggested (pending review) bucket, same as this feature\'s ordinary scheduled tick.'
    )) return;
    setAcApplyStarting(true);
    setAcApplyFailed(null);
    setAcApplyResult(null);
    setAcApplyProgress(null);
    try {
      const { data } = await matchingAPI.suggestedAutoconfirmStart(false);
      if (data.job_id) acApplyPoll.start(data.job_id);
    } catch (err) {
      setAcApplyFailed(getErrorMessage(err) || 'Failed to start apply.');
    } finally {
      setAcApplyStarting(false);
    }
  };

  const acApplyBusy = acApplyStarting || acApplyPoll.polling;

  const previewPoll = useJobPolling({
    fetchStatus: matchingAPI.receiptPaidReconciliationJobStatus,
    onCompleted: useCallback((result) => {
      setPreview(result);
      setPreviewFailed(null);
      setPreviewProgress(null);
    }, []),
    onFailed: useCallback((error) => {
      setPreviewFailed(error);
      setPreviewProgress(null);
    }, []),
    onProgress: useCallback((progress) => {
      setPreviewProgress(progress);
    }, []),
  });

  const applyPoll = useJobPolling({
    fetchStatus: matchingAPI.receiptPaidReconciliationJobStatus,
    onCompleted: useCallback((result) => {
      setApplyResult(result);
      setApplyFailed(null);
      setApplyProgress(null);
      refreshAwaitingReviewCount();
      refreshSuggestedList();
    }, [refreshAwaitingReviewCount, refreshSuggestedList]),
    onFailed: useCallback((error) => {
      setApplyFailed(error);
      setApplyProgress(null);
    }, []),
    onProgress: useCallback((progress) => {
      setApplyProgress(progress);
    }, []),
  });

  // Pick up an already-running Preview/Apply job on page load/refresh so
  // the buttons stay correctly disabled and results aren't lost.
  useJobResume({ fetchLatest: useCallback(() => matchingAPI.receiptPaidReconciliationJobLatest(true), []), poll: previewPoll });
  useJobResume({ fetchLatest: useCallback(() => matchingAPI.receiptPaidReconciliationJobLatest(false), []), poll: applyPoll });

  const runPreview = async () => {
    setPreviewStarting(true);
    setPreviewFailed(null);
    setPreviewProgress(null);
    setApplyResult(null);
    setApplyFailed(null);
    try {
      const { data } = await matchingAPI.receiptPaidReconciliationStart(true);
      if (data.job_id) {
        previewPoll.start(data.job_id);
      }
    } catch (err) {
      setPreviewFailed(getErrorMessage(err) || 'Failed to start preview.');
    } finally {
      setPreviewStarting(false);
    }
  };

  const previewBusy = previewStarting || previewPoll.polling;

  // Actionable outcomes an Apply click actually writes something for: a
  // real reconcile (tallies.reconciled), closing an already-reconciled-
  // externally receipt (tallies.already_reconciled_externally) -- gating
  // the button on tallies.reconciled alone hid it whenever every actionable
  // row was the latter (prod: 11 already_reconciled_externally, 0
  // reconciled), leaving no UI control for an action the card's own
  // description promises ("Apply here just runs it now") -- or creating a
  // suggested-only Match for a reparse-resolved-but-still-pending_review
  // receipt (tallies.suggested_pending_review, "scan, don't auto-confirm":
  // Apply DOES write here, it just never flips matching_status without a
  // human confirming the suggestion first). settled_via_rent is the SAME
  // Apply-time write as already_reconciled_externally (a narrower
  // provenance of the same pre-classification hit, see backend
  // _is_rent_settlement) -- omitting it here would reintroduce the exact
  // bug already fixed once for already_reconciled_externally, just for a
  // preview whose only actionable rows are rent settlements.
  const reconciledCount = preview?.tallies?.reconciled || 0;
  const alreadyReconciledExternallyCount = preview?.tallies?.already_reconciled_externally || 0;
  const settledViaRentCount = preview?.tallies?.settled_via_rent || 0;
  const suggestedPendingReviewCount = preview?.tallies?.suggested_pending_review || 0;
  const applyActionableCount = (
    reconciledCount + alreadyReconciledExternallyCount + settledViaRentCount + suggestedPendingReviewCount
  );

  const runApply = async () => {
    if (!window.confirm(
      `Reconcile ${reconciledCount} paid-but-pending-verification receipt(s) against the bank ` +
      `feed, close ${alreadyReconciledExternallyCount} already-reconciled-externally receipt(s), ` +
      `close ${settledViaRentCount} settled-via-rent receipt(s), ` +
      `and create ${suggestedPendingReviewCount} suggested (pending review) match(es) awaiting a ` +
      'human confirm now? Ambiguous/no-candidate receipts stay pending with a reason stamped on ' +
      'the invoice, never silently skipped.'
    )) return;
    setApplyStarting(true);
    setApplyFailed(null);
    setApplyResult(null);
    setApplyProgress(null);
    try {
      const previewedInvoiceIds = (preview?.reconciled || []).map((r) => r.invoice_id);
      const { data } = await matchingAPI.receiptPaidReconciliationStart(false, previewedInvoiceIds);
      if (data.job_id) {
        applyPoll.start(data.job_id);
      }
    } catch (err) {
      setApplyFailed(getErrorMessage(err) || 'Failed to start apply.');
    } finally {
      setApplyStarting(false);
    }
  };

  const applyBusy = applyStarting || applyPoll.polling;

  // Suggestion backlog hygiene (Prompt, 2026-08-26 v2): read-only
  // measurement plus three independent Preview/Apply chained passes,
  // meant to run in order (duplicate supersede -> orphan closeout ->
  // legacy-null mapping) before the widened tab above is trusted as the
  // true actionable queue. Every Apply's onCompleted refreshes the
  // measurement + suggested list + count + census so the operator watches
  // the backlog shrink in place.
  const measurementChain = useSuggestionChain({
    chainStart: matchingAPI.suggestedBacklogHygieneMeasurementChainStart,
    chainStatus: matchingAPI.suggestedBacklogHygieneMeasurementChainStatus,
    chainStop: matchingAPI.suggestedBacklogHygieneMeasurementChainStop,
    jobLatestOperation: 'measurement',
  });

  const refreshAfterHygieneApply = () => {
    measurementChain.run();
    refreshSuggestedList();
    refreshAwaitingReviewCount();
    censusChain.run();
  };

  const dedupPreviewChain = useSuggestionChain({
    chainStart: matchingAPI.suggestedDuplicateSupersedePreviewChainStart,
    chainStatus: matchingAPI.suggestedDuplicateSupersedePreviewChainStatus,
    chainStop: matchingAPI.suggestedDuplicateSupersedePreviewChainStop,
    jobLatestOperation: 'dedup_preview',
  });
  const dedupApplyChain = useSuggestionChain({
    chainStart: matchingAPI.suggestedDuplicateSupersedeApplyChainStart,
    chainStatus: matchingAPI.suggestedDuplicateSupersedeApplyChainStatus,
    chainStop: matchingAPI.suggestedDuplicateSupersedeApplyChainStop,
    jobLatestOperation: 'dedup_apply',
    onCompleted: (completedChain) => {
      toast.success(`Superseded ${completedChain?.seed?.tallies?.superseded ?? 0} duplicate suggestion(s)`);
      refreshAfterHygieneApply();
    },
  });
  const handleApplyDedup = () => {
    const t = dedupPreviewChain.result?.tallies;
    if (!t || t.groups_with_duplicates === 0) return;
    if (!window.confirm(
      `Supersede duplicate suggestions? ${t.groups_with_duplicates} receipt(s) have more than one ` +
      'suggestion of the same shape from repeated passes -- only the newest per receipt survives, ' +
      'the rest are superseded (reversible, never deleted).'
    )) return;
    dedupApplyChain.run();
  };

  const orphanPreviewChain = useSuggestionChain({
    chainStart: matchingAPI.suggestedOrphanCloseoutPreviewChainStart,
    chainStatus: matchingAPI.suggestedOrphanCloseoutPreviewChainStatus,
    chainStop: matchingAPI.suggestedOrphanCloseoutPreviewChainStop,
    jobLatestOperation: 'orphan_preview',
  });
  const orphanApplyChain = useSuggestionChain({
    chainStart: matchingAPI.suggestedOrphanCloseoutApplyChainStart,
    chainStatus: matchingAPI.suggestedOrphanCloseoutApplyChainStatus,
    chainStop: matchingAPI.suggestedOrphanCloseoutApplyChainStop,
    jobLatestOperation: 'orphan_apply',
    onCompleted: (completedChain) => {
      toast.success(`Closed out ${completedChain?.seed?.tallies?.superseded ?? 0} orphaned suggestion(s)`);
      refreshAfterHygieneApply();
    },
  });
  const handleApplyOrphan = () => {
    const t = orphanPreviewChain.result?.tallies;
    if (!t || t.orphaned === 0) return;
    if (!window.confirm(
      `Close out ${t.orphaned} orphaned suggestion(s)? Each one's receipt is no longer live in ` +
      'either candidate population this matcher reads (already reconciled another way, ' +
      'quarantined/rejected, predates the 2026 floor, ...) -- superseded, never deleted.'
    )) return;
    orphanApplyChain.run();
  };

  const legacyNullPreviewChain = useSuggestionChain({
    chainStart: matchingAPI.suggestedLegacyNullMappingPreviewChainStart,
    chainStatus: matchingAPI.suggestedLegacyNullMappingPreviewChainStatus,
    chainStop: matchingAPI.suggestedLegacyNullMappingPreviewChainStop,
    jobLatestOperation: 'legacynull_preview',
  });
  const legacyNullApplyChain = useSuggestionChain({
    chainStart: matchingAPI.suggestedLegacyNullMappingApplyChainStart,
    chainStatus: matchingAPI.suggestedLegacyNullMappingApplyChainStatus,
    chainStop: matchingAPI.suggestedLegacyNullMappingApplyChainStop,
    jobLatestOperation: 'legacynull_apply',
    onCompleted: (completedChain) => {
      const t = completedChain?.seed?.tallies;
      toast.success(`Mapped ${t?.mapped ?? 0}, superseded ${t?.superseded ?? 0}`);
      refreshAfterHygieneApply();
    },
  });
  const handleApplyLegacyNull = () => {
    const t = legacyNullPreviewChain.result?.tallies;
    if (!t || (t.mapped === 0 && t.superseded === 0)) return;
    if (!window.confirm(
      `Map ${t.mapped} legacy reasonless suggestion(s) to reparse-resolved and supersede ` +
      `${t.superseded} more (already covered by a newer suggestion or a terminal receipt)? ` +
      `${t.unmapped} more have no confirmed mapping and are left untouched, listed for a human.`
    )) return;
    legacyNullApplyChain.run();
  };

  // Per-reason count chips (Implementation item 4) + cohort grouping for
  // the tab's own row list, computed from the same suggestedItems state --
  // display-only, never a second population query.
  const suggestedReasonCounts = {};
  suggestedItems.forEach((item) => {
    const r = item.awaiting_review_reason || 'unknown';
    suggestedReasonCounts[r] = (suggestedReasonCounts[r] || 0) + 1;
  });
  const groupedSuggestedItems = groupSuggestedItemsForDisplay(suggestedItems);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Receipt className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Receipt-Paid Reconciliation</CardTitle>
          {awaitingReviewCount > 0 && (
            <Badge
              variant="outline"
              className="border-blue-400 text-blue-700 dark:text-blue-400"
              data-testid="receipt-paid-awaiting-review-badge"
              title="Suggested matches created by 'scan, don't auto-confirm' still waiting on a human confirm in the ordinary matches queue"
            >
              {awaitingReviewCount} awaiting your review
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          Auto-verifies "Paid - pending verification" tool-subscription receipts (e.g.
          Anthropic) AND Unpaid invoices past their due date (e.g. Xero (UK) Ltd direct debits --
          a corroborated match marks these Paid too, tagged matched-from-unpaid) against the bank
          feed: the receipt's own entity is searched first (Tier 1), widening to every entity only
          when Tier 1 has no candidate (Tier 2). A single candidate in the deciding tier, within
          fee/FX tolerance (any currency pair, via cache or a real dated live rate -- never a raw
          number when no rate exists) and a vendor-name match (exact, core-token, or high
          similarity), reconciles it. More than one candidate in the deciding tier stays pending
          unless the gap to the next-closest date exceeds 24h -- unless several receipts share the
          same vendor/amount/currency (a recurring subscription), in which case the whole cohort is
          solved as a one-to-one nearest-date assignment instead; a leftover receipt with no
          remaining candidate stays pending as cohort unbalanced, never forced. Before searching for
          a new candidate at all, checks whether the receipt's true settling transaction is already
          reconciled elsewhere (a different matcher, or a sibling receipt) -- if so it is classified
          "already reconciled externally", never a misleading no-candidate/ambiguous. A receipt whose
          own search window ends before every connected account's own bank-feed history starts (an
          old invoice ingested well after the fact) is reported as "predates feed coverage" instead
          of a genuine no-candidate miss -- it can never match until the feed itself backfills
          further. A receipt whose amount was regex-resolved by the Gmail reparse pass and is still
          pending_review never auto-reconciles here ("scan, don't auto-confirm") -- a found candidate
          only creates a suggested match for a human to confirm via the ordinary matches queue.
          Also runs automatically on the existing auto-matching schedule; Apply here just
          runs it now. Preview/Apply run in the background and this panel polls for progress -- it is
          safe to navigate away and come back.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {suggestedItems.length > 0 && (
          <div className="border rounded p-2 space-y-1" data-testid="suggested-pending-review-bucket">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={allSuggestedSelected}
                  onCheckedChange={handleToggleSelectAllSuggested}
                  aria-label="Select all suggested (pending review) matches"
                  data-testid="suggested-pending-review-select-all"
                />
                <span className="text-sm font-medium" data-testid="suggested-pending-review-header">
                  Suggested (pending review) -- {suggestedItems.length} waiting
                </span>
                <div className="flex items-center gap-1 flex-wrap" data-testid="suggested-reason-count-chips">
                  {Object.entries(suggestedReasonCounts).map(([reason, count]) => (
                    <Badge
                      key={reason}
                      variant={AWAITING_REVIEW_REASON_CHIP_VARIANT[reason] || 'outline'}
                      className="text-xs"
                      data-testid={'reason-count-chip-' + reason}
                      title={AWAITING_REVIEW_REASON_LABELS[reason] || reason}
                    >
                      {(AWAITING_REVIEW_REASON_CHIP_LABELS[reason] || reason)}: {count}
                    </Badge>
                  ))}
                </div>
              </div>
              {selectedSuggestedIds.size > 0 && (
                <Button
                  size="sm" variant="default" className="bg-green-600 hover:bg-green-700"
                  disabled={suggestedActionBusy}
                  onClick={handleBulkConfirmSuggested}
                  data-testid="suggested-pending-review-confirm-selected"
                >
                  {suggestedActionBusy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
                  Confirm selected ({selectedSuggestedIds.size})
                </Button>
              )}
            </div>
            <div
              className="flex items-center gap-2 flex-wrap border-t pt-2 text-xs"
              data-testid="vendor-text-cohort-recompute-controls"
            >
              <Button
                size="sm" variant="outline" disabled={cohortRecomputeBusy}
                onClick={handlePreviewCohortRecompute}
                data-testid="vendor-text-cohort-recompute-preview-btn"
              >
                {cohortRecomputeBusy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
                Preview vendor-text cohort recompute
              </Button>
              {cohortRecomputePreview && (
                <>
                  <span data-testid="vendor-text-cohort-recompute-summary">
                    scanned {cohortRecomputePreview.tallies.scanned}, cohorts{' '}
                    {cohortRecomputePreview.tallies.cohorts_examined}, would reassign{' '}
                    {cohortRecomputePreview.tallies.reassigned}, unchanged{' '}
                    {cohortRecomputePreview.tallies.unchanged}, would flag unassigned{' '}
                    {cohortRecomputePreview.tallies.flagged_unassigned}
                  </span>
                  {(cohortRecomputePreview.tallies.reassigned > 0
                    || cohortRecomputePreview.tallies.flagged_unassigned > 0) && (
                    <Button
                      size="sm" variant="default" className="bg-amber-600 hover:bg-amber-700"
                      disabled={cohortRecomputeBusy}
                      onClick={handleApplyCohortRecompute}
                      data-testid="vendor-text-cohort-recompute-apply-btn"
                    >
                      Apply recompute
                    </Button>
                  )}
                </>
              )}
            </div>
            <div data-testid="suggested-pending-review-grouped-list">
              {groupedSuggestedItems.map((group) => (
                <div key={group.key}>
                  {group.isCohort && (
                    <div
                      className="text-xs text-muted-foreground pt-2 first:pt-0"
                      data-testid={'cohort-group-header-' + group.key}
                    >
                      cohort: {group.items.length} receipts
                    </div>
                  )}
                  {group.items.map((item) => (
                    <SuggestedPendingReviewRow
                      key={item.match_id}
                      item={item}
                      selected={selectedSuggestedIds.has(item.match_id)}
                      onToggleSelect={handleToggleSelectSuggested}
                      onConfirm={handleConfirmOneSuggested}
                      onReject={handleRejectOneSuggested}
                      busy={suggestedActionBusy}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="border rounded p-2 space-y-2 text-xs" data-testid="suggestion-backlog-hygiene-section">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="text-sm font-medium">Suggestion backlog hygiene</span>
            <Button
              size="sm" variant="outline" disabled={measurementChain.busy}
              onClick={measurementChain.run}
              data-testid="hygiene-measurement-refresh-btn"
            >
              {measurementChain.busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
              {measurementChain.running ? 'Running...' : 'Refresh'}
            </Button>
          </div>
          <p className="text-muted-foreground">
            Read-only measurement per suggestion shape family, then three independent Preview/Apply
            passes -- run in order (duplicate supersede, then orphan closeout, then legacy-null
            mapping) before trusting the widened bucket above as the true actionable queue. Runs as
            a budget-boxed background pass (safe to navigate away and come back) -- nothing is ever
            deleted, superseded suggestions stay in the database, reversible.
          </p>
          <ChainProgressLine chain={measurementChain} testIdPrefix="hygiene-measurement" />
          {measurementChain.result && (
            <div className="overflow-x-auto" data-testid="hygiene-measurement-table">
              <div
                className="grid gap-x-2 text-muted-foreground font-medium"
                style={{ gridTemplateColumns: 'repeat(6, max-content)' }}
              >
                <div>family</div>
                <div>docs</div>
                <div>distinct receipts</div>
                <div>duplicate docs</div>
                <div>orphaned receipts</div>
                <div>invoice missing</div>
              </div>
              {(measurementChain.result.families_report || []).map((f, i) => (
                <div
                  key={f.family || i} data-testid={'hygiene-measurement-row-' + i}
                  className="grid gap-x-2"
                  style={{ gridTemplateColumns: 'repeat(6, max-content)' }}
                >
                  <div>{f.family}</div>
                  <div>{f.doc_count}</div>
                  <div>{f.distinct_receipt_count}</div>
                  <div>{f.duplicate_doc_count}</div>
                  <div>{f.orphaned_receipt_count}</div>
                  <div>{f.orphan_invoice_missing_count}</div>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap border-t pt-2" data-testid="dedup-controls">
            <Button
              size="sm" variant="outline" disabled={dedupPreviewChain.busy}
              onClick={dedupPreviewChain.run}
              data-testid="dedup-preview-btn"
            >
              {dedupPreviewChain.busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
              Preview duplicate supersede
            </Button>
            <ChainProgressLine chain={dedupPreviewChain} testIdPrefix="dedup-preview" />
            {dedupPreviewChain.result && (
              <>
                <span data-testid="dedup-summary">
                  scanned {dedupPreviewChain.result.tallies.scanned}, groups with duplicates{' '}
                  {dedupPreviewChain.result.tallies.groups_with_duplicates}
                </span>
                {dedupPreviewChain.result.tallies.groups_with_duplicates > 0 && (
                  <Button
                    size="sm" variant="default" className="bg-amber-600 hover:bg-amber-700"
                    disabled={dedupApplyChain.busy}
                    onClick={handleApplyDedup}
                    data-testid="dedup-apply-btn"
                  >
                    {dedupApplyChain.busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
                    Apply duplicate supersede
                  </Button>
                )}
              </>
            )}
            <ChainProgressLine chain={dedupApplyChain} testIdPrefix="dedup-apply" />
          </div>

          <div className="flex items-center gap-2 flex-wrap border-t pt-2" data-testid="orphan-controls">
            <Button
              size="sm" variant="outline" disabled={orphanPreviewChain.busy}
              onClick={orphanPreviewChain.run}
              data-testid="orphan-preview-btn"
            >
              {orphanPreviewChain.busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
              Preview orphan closeout
            </Button>
            <ChainProgressLine chain={orphanPreviewChain} testIdPrefix="orphan-preview" />
            {orphanPreviewChain.result && (
              <>
                <span data-testid="orphan-summary">
                  scanned {orphanPreviewChain.result.tallies.scanned}, orphaned{' '}
                  {orphanPreviewChain.result.tallies.orphaned}
                </span>
                {orphanPreviewChain.result.tallies.orphaned > 0 && (
                  <Button
                    size="sm" variant="default" className="bg-amber-600 hover:bg-amber-700"
                    disabled={orphanApplyChain.busy}
                    onClick={handleApplyOrphan}
                    data-testid="orphan-apply-btn"
                  >
                    {orphanApplyChain.busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
                    Apply orphan closeout
                  </Button>
                )}
              </>
            )}
            <ChainProgressLine chain={orphanApplyChain} testIdPrefix="orphan-apply" />
          </div>

          <div className="flex items-center gap-2 flex-wrap border-t pt-2" data-testid="legacy-null-controls">
            <Button
              size="sm" variant="outline" disabled={legacyNullPreviewChain.busy}
              onClick={legacyNullPreviewChain.run}
              data-testid="legacy-null-preview-btn"
            >
              {legacyNullPreviewChain.busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
              Preview legacy-null mapping
            </Button>
            <ChainProgressLine chain={legacyNullPreviewChain} testIdPrefix="legacy-null-preview" />
            {legacyNullPreviewChain.result && (
              <>
                <span data-testid="legacy-null-summary">
                  scanned {legacyNullPreviewChain.result.tallies.scanned}, mapped{' '}
                  {legacyNullPreviewChain.result.tallies.mapped}, superseded{' '}
                  {legacyNullPreviewChain.result.tallies.superseded}, unmapped{' '}
                  {legacyNullPreviewChain.result.tallies.unmapped}
                </span>
                {(legacyNullPreviewChain.result.tallies.mapped > 0
                  || legacyNullPreviewChain.result.tallies.superseded > 0) && (
                  <Button
                    size="sm" variant="default" className="bg-amber-600 hover:bg-amber-700"
                    disabled={legacyNullApplyChain.busy}
                    onClick={handleApplyLegacyNull}
                    data-testid="legacy-null-apply-btn"
                  >
                    {legacyNullApplyChain.busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
                    Apply legacy-null mapping
                  </Button>
                )}
              </>
            )}
            <ChainProgressLine chain={legacyNullApplyChain} testIdPrefix="legacy-null-apply" />
          </div>
        </div>

        <div className="border rounded p-2 space-y-2 text-xs" data-testid="suggested-shape-census-section">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="text-sm font-medium">Suggestion doc shape census</span>
            <Button
              size="sm" variant="outline" disabled={censusChain.busy}
              onClick={censusChain.run}
              data-testid="shape-census-refresh-btn"
            >
              {censusChain.busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
              {censusChain.running ? 'Running...' : 'Refresh'}
            </Button>
          </div>
          <p className="text-muted-foreground">
            Read-only: every distinct stored shape of a plausible receipt-paid suggestion doc, so
            a mismatch between this tab's own count and any pass tally is diagnosable from real
            data instead of guessed at. Runs as a budget-boxed background pass, safe to navigate
            away and come back.
          </p>
          <ChainProgressLine chain={censusChain} testIdPrefix="shape-census" />
          {censusChain.failed && (
            <p className="text-red-600" data-testid="shape-census-error">{censusChain.failed}</p>
          )}
          {censusChain.result && (
            <>
              <p data-testid="shape-census-summary">
                scanned {censusChain.result.total_scanned} of {censusChain.result.total_matching_query}
                {censusChain.result.truncated ? ' (truncated -- see logs)' : ''}
              </p>
              {/* A real <table> here breaks under this project's dev
                  instrumentation (conditional children get wrapped in an
                  extra <span>, invalid as a direct child of <tbody>/<tr> --
                  CLAUDE.md memory: invalid HTML nesting only a live render
                  catches). A CSS-grid row list sidesteps that entirely. */}
              <div className="overflow-x-auto" data-testid="shape-census-table">
                <div
                  className="grid gap-x-2 text-muted-foreground font-medium"
                  style={{ gridTemplateColumns: 'repeat(8, max-content)' }}
                >
                  <div>status</div>
                  <div>confirmed_via</div>
                  <div>awaiting_review_reason</div>
                  <div>tier</div>
                  <div>reparse precedence?</div>
                  <div>count</div>
                  <div>visible?</div>
                  <div>classification</div>
                </div>
                {(censusChain.result.combinations || []).map((c, i) => (
                  <div
                    key={i} data-testid={'shape-census-row-' + i}
                    className="grid gap-x-2"
                    style={{ gridTemplateColumns: 'repeat(8, max-content)' }}
                  >
                    <div>{String(c.status)}</div>
                    <div>{String(c.confirmed_via)}</div>
                    <div>{String(c.awaiting_review_reason)}</div>
                    <div>{String(c.tier)}</div>
                    <div>{c.invoice_reparse_resolved_precedence ? 'yes' : 'no'}</div>
                    <div>{c.count}</div>
                    <div>{c.matches_shared_filter ? 'yes' : 'no'}</div>
                    <div>{c.classification}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          <div
            className="flex items-center gap-2 flex-wrap border-t pt-2"
            data-testid="reparse-precedence-repair-controls"
          >
            <Button
              size="sm" variant="outline" disabled={repairPreviewChain.busy}
              onClick={repairPreviewChain.run}
              data-testid="reparse-precedence-repair-preview-btn"
            >
              {repairPreviewChain.busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
              Preview reparse-precedence repair
            </Button>
            <ChainProgressLine chain={repairPreviewChain} testIdPrefix="reparse-precedence-repair" />
            {repairPreviewChain.result && (
              <>
                <span data-testid="reparse-precedence-repair-summary">
                  scanned {repairPreviewChain.result.tallies.scanned}, stale found{' '}
                  {repairPreviewChain.result.tallies.stale_found}
                </span>
                {repairPreviewChain.result.tallies.stale_found > 0 && (
                  <Button
                    size="sm" variant="default" className="bg-amber-600 hover:bg-amber-700"
                    disabled={repairApplyChain.busy}
                    onClick={handleApplyReparsePrecedenceRepair}
                    data-testid="reparse-precedence-repair-apply-btn"
                  >
                    {repairApplyChain.busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
                    Apply repair
                  </Button>
                )}
              </>
            )}
            <ChainProgressLine chain={repairApplyChain} testIdPrefix="reparse-precedence-repair-apply" />
          </div>
        </div>

        <div className="border rounded p-2 space-y-2" data-testid="suggested-autoconfirm-section">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="text-sm font-medium">Auto-confirm (deterministic tier-1 only)</span>
            <div className="flex items-center gap-1">
              {acStatus && (
                <Badge
                  variant="outline"
                  className={acStatus.enabled ? 'border-green-500 text-green-700 dark:text-green-400' : ''}
                  data-testid="suggested-autoconfirm-flag-badge"
                >
                  {acStatus.enabled ? 'enabled' : 'disabled'}
                </Badge>
              )}
              <Button
                size="sm" variant="outline" disabled={acFlagBusy || !acStatus || acStatus.enabled}
                onClick={() => toggleAcFlag(true)}
                data-testid="suggested-autoconfirm-enable"
              >
                Enable
              </Button>
              <Button
                size="sm" variant="outline" disabled={acFlagBusy || !acStatus || !acStatus.enabled}
                onClick={() => toggleAcFlag(false)}
                data-testid="suggested-autoconfirm-disable"
              >
                Disable
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Confirms a suggestion with zero clicks only when ALL of: exactly one candidate
            transaction and it is not shared with any other open suggestion, amount exact within
            tier-1 tolerance, vendor exact-after-qualifier-stripping or a curated alias (never a
            containment/fuzzy score alone), date inside the matcher's own window, the invoice's
            only pending-review reason is the reparse resolution, and its entity is resolved.
            Everything else stays in the Suggested (pending review) bucket above for a human.
          </p>

          {acStatus && (
            <div className="text-xs text-muted-foreground space-y-1" data-testid="suggested-autoconfirm-last-run">
              <div>
                Last run: auto-confirmed {acStatus.last_run_auto_confirmed ?? 0}, left for review{' '}
                {acStatus.last_run_left_for_review ?? 0}
              </div>
              {acStatus.last_run_breakdown && Object.keys(acStatus.last_run_breakdown).length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {Object.entries(acStatus.last_run_breakdown).map(([reason, count]) => (
                    <Badge key={reason} variant="outline" className="text-[10px]" data-testid={'suggested-autoconfirm-breakdown-' + reason}>
                      {reason}: {count}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex gap-2">
            <Button
              variant="outline" size="sm" onClick={runAcPreview} disabled={acPreviewBusy}
              data-testid="suggested-autoconfirm-preview"
            >
              {acPreviewBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Preview
            </Button>
            <Button
              variant="default" size="sm" onClick={runAcApply} disabled={acApplyBusy}
              data-testid="suggested-autoconfirm-apply"
            >
              {acApplyBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Run Now (Drain)
            </Button>
          </div>

          {acPreviewPoll.polling && (
            <JobProgressLine progress={acPreviewProgress} testId="suggested-autoconfirm-preview-progress">
              {(p) => <>Scanned {p.scanned ?? 0} of {p.total ?? '?'}</>}
            </JobProgressLine>
          )}
          <JobErrorBlock error={acPreviewFailed} testId="suggested-autoconfirm-preview-error" />

          {acPreview && (
            <div className="space-y-2" data-testid="suggested-autoconfirm-preview-result">
              <p className="text-xs text-muted-foreground">
                Seen {acPreview.seen ?? 0} of {acPreview.true_population_count ?? 0}
                {acPreview.population_truncated && (
                  <span className="text-amber-700 dark:text-amber-400"> · stopped early at the safety cap</span>
                )}
                {' '}· would auto-confirm {acPreview.auto_confirmed ?? 0} · left for review {acPreview.left_for_review ?? 0}
              </p>
              {acPreview.qualifying && acPreview.qualifying.length > 0 && (
                <ul className="text-xs space-y-1" data-testid="suggested-autoconfirm-qualifying-list">
                  {acPreview.qualifying.map((q) => (
                    <li key={q.match_id} className="font-mono text-green-700 dark:text-green-400">
                      {q.invoice_id} · tx {q.transaction_id} ·{' '}
                      {q.evidence?.invoice_vendor} = {q.evidence?.transaction_vendor} ·{' '}
                      {q.evidence?.invoice_amount} vs {q.evidence?.tx_amount}
                    </li>
                  ))}
                </ul>
              )}
              {acPreview.non_qualifying && acPreview.non_qualifying.length > 0 && (
                <ul className="text-xs space-y-1" data-testid="suggested-autoconfirm-non-qualifying-list">
                  {acPreview.non_qualifying.map((n) => (
                    <li key={n.match_id} className="font-mono text-muted-foreground">
                      {n.invoice_id} · {(n.reasons || []).join(', ')}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {acApplyPoll.polling && (
            <JobProgressLine progress={acApplyProgress} testId="suggested-autoconfirm-apply-progress">
              {(p) => <>Scanned {p.scanned ?? 0} of {p.total ?? '?'}</>}
            </JobProgressLine>
          )}
          <JobErrorBlock error={acApplyFailed} testId="suggested-autoconfirm-apply-error" />

          {acApplyResult && !acApplyFailed && (
            <div
              className="p-3 rounded text-xs border bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-900"
              data-testid="suggested-autoconfirm-apply-result"
            >
              <p><strong>Auto-confirmed:</strong> {acApplyResult.auto_confirmed ?? 0}</p>
              <p><strong>Left for review:</strong> {acApplyResult.left_for_review ?? 0}</p>
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline" size="sm" onClick={runPreview} disabled={previewBusy}
            data-testid="receipt-paid-reconciliation-preview"
          >
            {previewBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Preview
          </Button>
          {preview && !preview.error && applyActionableCount > 0 && (
            <Button
              variant="default" size="sm" onClick={runApply}
              disabled={applyBusy}
              data-testid="receipt-paid-reconciliation-apply"
            >
              {applyBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Apply ({applyActionableCount})
            </Button>
          )}
        </div>

        {previewPoll.polling && (
          <JobProgressLine progress={previewProgress} testId="receipt-paid-reconciliation-preview-progress">
            {(p) => <>Scanned {p.scanned ?? 0} of {p.total ?? '?'} receipts ({p.seen ?? 0} in population)</>}
          </JobProgressLine>
        )}

        <JobErrorBlock error={previewFailed} testId="receipt-paid-reconciliation-preview-error" />

        {preview && (
          <div className="space-y-2" data-testid="receipt-paid-reconciliation-preview-result">
            {preview.true_population_count != null && (
              <p className="text-xs text-muted-foreground" data-testid="receipt-paid-population-summary">
                Scanned {preview.tallies?.seen ?? 0} of {preview.true_population_count} pending receipts
                {preview.population_truncated && (
                  <span className="text-amber-700 dark:text-amber-400">
                    {' '}· stopped early at the safety cap, {preview.true_population_count - (preview.tallies?.seen ?? 0)} remain for the next run
                  </span>
                )}
              </p>
            )}
            {preview.predates_matching_floor_count > 0 && (
              <Badge variant="outline" className="text-[10px]" data-testid="receipt-paid-predates-matching-floor">
                Predates 2026 matching floor: {preview.predates_matching_floor_count} (excluded, not deleted)
              </Badge>
            )}
            <div className="flex flex-wrap gap-2">
              {preview.tallies && Object.entries(preview.tallies).map(([key, count]) => (
                <Badge key={key} variant="outline" className="text-[10px]" data-testid={'receipt-paid-tally-' + key}>
                  {TALLY_LABELS[key] || key}: {count}
                </Badge>
              ))}
            </div>
            {preview.reconciled && preview.reconciled.length > 0 && (
              <ul className="text-xs space-y-1" data-testid="receipt-paid-reconciled-list">
                {preview.reconciled.map((r) => (
                  <li key={r.invoice_id} className="font-mono">
                    {r.invoice_id} · {r.supplier} · {r.amount} · tx {r.transaction_id}
                    {r.tier && <> · {TIER_LABELS[r.tier] || r.tier}</>}
                    {r.tie_broken && <> · tie-broken</>}
                    {r.gap_hours != null && <> · gap {r.gap_hours}h{r.cohort_size ? ` (cohort of ${r.cohort_size})` : ''}</>}
                    {r.candidates_truncated && (
                      <span className="text-amber-700 dark:text-amber-400"> · candidate scan truncated</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {preview.suggested_pending_review && preview.suggested_pending_review.length > 0 && (
              <ul className="text-xs space-y-1" data-testid="receipt-paid-suggested-pending-review-list">
                {preview.suggested_pending_review.map((s) => (
                  <li key={s.invoice_id} className="font-mono text-blue-700 dark:text-blue-400">
                    {s.invoice_id} · {s.supplier} · {s.amount} · candidate tx {s.transaction_id} ·{' '}
                    {AWAITING_REVIEW_REASON_LABELS[s.awaiting_review_reason]
                      || 'awaiting human confirm in the matches review queue'}
                    {s.tier && <> · {TIER_LABELS[s.tier] || s.tier}</>}
                    {s.tie_broken && <> · tie-broken</>}
                  </li>
                ))}
              </ul>
            )}
            {preview.already_reconciled_externally && preview.already_reconciled_externally.length > 0 && (
              <ul className="text-xs space-y-1" data-testid="receipt-paid-already-reconciled-externally-list">
                {preview.already_reconciled_externally.map((r) => (
                  <li key={r.invoice_id} className="font-mono text-blue-700 dark:text-blue-400">
                    {r.invoice_id} · {r.supplier} · {r.amount} · already reconciled via tx {r.transaction_id}
                    {r.transaction_date && <> ({r.transaction_date})</>}
                    {r.tier && <> · {TIER_LABELS[r.tier] || r.tier}</>}
                    {r.reconciled_by?.sibling_receipt_id ? (
                      <> · reconciled against sibling receipt {r.reconciled_by.sibling_receipt_id}</>
                    ) : r.reconciled_by?.pointer_value ? (
                      <> · reconciled_by {r.reconciled_by.kind || r.reconciled_by.pointer_field}: {r.reconciled_by.pointer_value}</>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
            {preview.settled_via_rent && preview.settled_via_rent.length > 0 && (
              <ul className="text-xs space-y-1" data-testid="receipt-paid-settled-via-rent-list">
                {preview.settled_via_rent.map((r) => (
                  <li key={r.invoice_id} className="font-mono text-teal-700 dark:text-teal-400">
                    {r.invoice_id} · {r.supplier} · {r.amount} · settled via rent tx {r.transaction_id}
                    {r.transaction_date && <> ({r.transaction_date})</>}
                    {r.tier && <> · {TIER_LABELS[r.tier] || r.tier}</>}
                    {r.reconciled_by?.month && (
                      <> · Rent · {r.reconciled_by.month}</>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {preview.ambiguous && preview.ambiguous.length > 0 && (
              <ul className="text-xs space-y-1" data-testid="receipt-paid-ambiguous-list">
                {preview.ambiguous.map((a) => (
                  <li key={a.invoice_id} className="font-mono text-amber-700 dark:text-amber-400">
                    {a.invoice_id} · {a.supplier} · {a.candidate_count} candidates
                    {a.tier && <> ({TIER_LABELS[a.tier] || a.tier})</>}
                    {a.candidates_truncated && <> · candidate scan truncated</>}
                  </li>
                ))}
              </ul>
            )}
            {preview.window_predates_feed_coverage && preview.window_predates_feed_coverage.length > 0 && (
              <ul className="text-xs space-y-1" data-testid="receipt-paid-window-predates-feed-coverage-list">
                {preview.window_predates_feed_coverage.map((w) => (
                  <li key={w.invoice_id} className="font-mono text-slate-600 dark:text-slate-400">
                    {w.invoice_id} · {w.supplier} · window ends {w.window_end}, before every connected
                    account's own feed history
                    {w.earliest_coverage_by_account && w.earliest_coverage_by_account.length > 0 && (
                      <> (earliest: {w.earliest_coverage_by_account
                        .map((a) => `${a.bank_name || a.account_name || a.bank_account_id} from ${a.earliest_coverage_date}`)
                        .join(', ')})</>
                    )}
                    {' '}· permanently unmatchable unless the feed backfills further
                  </li>
                ))}
              </ul>
            )}
            {preview.no_candidate && preview.no_candidate.length > 0 && (
              <ul className="text-xs space-y-1" data-testid="receipt-paid-no-candidate-list">
                {preview.no_candidate.map((n) => (
                  <li key={n.invoice_id} className="font-mono text-muted-foreground">
                    {n.invoice_id} · {n.supplier} · no candidate found
                    {n.candidates_truncated && (
                      <span className="text-amber-700 dark:text-amber-400"> · candidate scan truncated</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {preview.fx_rate_unavailable && preview.fx_rate_unavailable.length > 0 && (
              <ul className="text-xs space-y-1" data-testid="receipt-paid-fx-rate-unavailable-list">
                {preview.fx_rate_unavailable.map((f) => (
                  <li key={f.invoice_id} className="font-mono text-amber-700 dark:text-amber-400">
                    {f.invoice_id} · {f.supplier} · a candidate transaction exists but no FX rate could price it
                  </li>
                ))}
              </ul>
            )}
            {preview.cohort_unbalanced && preview.cohort_unbalanced.length > 0 && (
              <ul className="text-xs space-y-1" data-testid="receipt-paid-cohort-unbalanced-list">
                {preview.cohort_unbalanced.map((u) => (
                  <li key={u.invoice_id} className="font-mono text-amber-700 dark:text-amber-400">
                    {u.invoice_id} · {u.supplier} · cohort of {u.cohort_size}, {u.candidate_count} candidate(s) claimed by a closer sibling
                    {u.candidates_truncated && <> · candidate scan truncated</>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {applyPoll.polling && (
          <JobProgressLine progress={applyProgress} testId="receipt-paid-reconciliation-apply-progress">
            {(p) => <>Scanned {p.scanned ?? 0} of {p.total ?? '?'} receipts ({p.seen ?? 0} in population)</>}
          </JobProgressLine>
        )}

        <JobErrorBlock error={applyFailed} testId="receipt-paid-reconciliation-apply-error" />

        {applyResult && !applyFailed && (
          <div
            className="p-3 rounded text-xs border bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-900"
            data-testid="receipt-paid-reconciliation-apply-result"
          >
            <p><strong>Reconciled:</strong> {applyResult.tallies?.reconciled ?? 0}</p>
            {applyResult.tallies?.already_reconciled_externally > 0 && (
              <p data-testid="receipt-paid-already-reconciled-externally-apply-count">
                <strong>Already reconciled externally:</strong>{' '}
                {applyResult.tallies.already_reconciled_externally}
              </p>
            )}
            {applyResult.tallies?.settled_via_rent > 0 && (
              <p data-testid="receipt-paid-settled-via-rent-apply-count">
                <strong>Settled via rent:</strong>{' '}
                {applyResult.tallies.settled_via_rent}
              </p>
            )}
            {applyResult.tallies?.already_reconciled_since_preview > 0 && (
              <p data-testid="receipt-paid-already-reconciled-since-preview">
                <strong>Already reconciled since preview:</strong>{' '}
                {applyResult.tallies.already_reconciled_since_preview}
              </p>
            )}
            {/* Actionable subset only (awaiting_review_reason ==
                reparse-resolved) -- the SAME predicate the "Suggested
                (pending review)" bucket/count/drain above read, never the
                raw across-all-reasons tally (that count includes rows this
                pass suggested for OTHER review reasons -- reference/
                vendor-text amount gaps, brand-token -- that never appear in
                this bucket by design; see suggested_pending_review_
                shape_census above for the full breakdown). */}
            {applyResult.tallies?.suggested_pending_review_actionable > 0 && (
              <p data-testid="receipt-paid-suggested-pending-review-apply-count">
                <strong>Suggested (pending review):</strong>{' '}
                {applyResult.tallies.suggested_pending_review_actionable}
              </p>
            )}
            {(applyResult.tallies?.suggested_pending_review || 0)
              > (applyResult.tallies?.suggested_pending_review_actionable || 0) && (
              <p data-testid="receipt-paid-suggested-other-reason-apply-count">
                <strong>Suggested (other review reason, not in this bucket):</strong>{' '}
                {(applyResult.tallies.suggested_pending_review || 0)
                  - (applyResult.tallies.suggested_pending_review_actionable || 0)}
              </p>
            )}
            {applyResult.predates_matching_floor_count > 0 && (
              <p data-testid="receipt-paid-predates-matching-floor-apply-count">
                <strong>Predates 2026 matching floor:</strong>{' '}
                {applyResult.predates_matching_floor_count} (excluded, not deleted)
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
